import BusinessIcon from "@mui/icons-material/Business";
import { Description } from "@mui/icons-material";
import { Box, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { getGrossLineFromCostOverride } from "../../../../../scope-of-work/components/ListTable/components/invoiceLineFromCostOverride";
import { colors } from "../../../../../ui/colors";
import { normalizeCostOverride } from "./BasicJob";
import { JobScopeOfWorkView } from "./JobScopeOfWorkView";

export const AdvancedJob = ({ job }) => {
  const alias = useSelector((state: RootState) => state.quoteCreate.sectorWorkflow.alias || "");
  const scope = job.scopeOfWork;
  if (!scope) return null;

  const typeService = scope.typeOfService;
  if (!typeService) return null;

  const cost = normalizeCostOverride(scope.costOverride) || {};

  const rawUnitPrice = Number(cost.value ?? cost.price ?? cost.amount ?? 0);
  const unitPrice = Number.isFinite(rawUnitPrice) ? rawUnitPrice : 0;
  const rawUnits = cost.units != null && cost.units !== "" ? Number(cost.units) : 1;
  const units = Number.isFinite(rawUnits) && rawUnits > 0 ? rawUnits : unitPrice > 0 ? 1 : 0;
  const currency = cost.currency || "USD";
  const total = getGrossLineFromCostOverride({ ...cost, units }) ?? units * unitPrice;
  const formattedUnitPrice = unitPrice.toLocaleString();
  const formattedTotal = total.toLocaleString();

  const frequency = scope.frequencyType || "N/A";

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid var(--fx-surface-border, #E2E8F0)",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
      elevation={0}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
              border: "1px solid #A9D4FF",
              color: "#1688F0",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <BusinessIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: "var(--fx-surface-text, #0F172A)" }}>{typeService.name}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: colors.orange.base }}>Service</Typography>
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: 18, color: colors.orange.base, whiteSpace: "nowrap" }}>${formattedTotal}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(5, minmax(0, 1fr))" }, gap: 2, alignItems: "start" }}>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Unit Price
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            ${formattedUnitPrice} {currency}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Units
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{units}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Total
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: colors.orange.base }}>${formattedTotal}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Frequency
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.orange.base }}>{frequency}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Actions
          </Typography>
          <BaseModalComponent
            modalKey={`job-scope-${scope.uid}`}
            modalLabel={`${typeService.name} – Scope of Work`}
            defaultSizes={{
              width: { xs: "95%", md: "1100px" },
              height: { xs: "90%", md: "90%" },
            }}
            elementTrigger={
              <Tooltip title="View scope details">
                <IconButton
                  size="small"
                  sx={{
                    mt: 0.25,
                    color: colors.orange.base,
                    border: `1.5px solid ${colors.orange.base}`,
                    borderRadius: 1,
                  }}
                >
                  <Description fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <JobScopeOfWorkView scope={scope} />
          </BaseModalComponent>
        </Box>
      </Box>
    </Paper>
  );
};
